import {HttpClient, HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HttpClientService } from './http-client.service';
import { API_CONSTANT } from './api.constant';
import {Observable} from 'rxjs/Observable';
import {catchError} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';

@Injectable()
export class ApiService {
  constructor(private localHttp: HttpClient,
              private http: HttpClientService,
              private translate: TranslateService) {
  }

  getErrorMessage(error, delimiter?) {
    if (error != null && error.error != null && error.error.message != null && error.error.message.length > 0) {
      return delimiter ? error.error.message.join(delimiter) : error.error.message.join('.');
    } else if (error != null && error.error != null && error.error.non_field_errors != null && error.error.non_field_errors.length > 0) {
      return error.error.non_field_errors.join(',');
    } else {
      return this.translate.instant('ERROR.TRY-AGAIN');
    }
  }

  createDownloadFile(blob, filename) {
    // for IE10+
    if (typeof navigator !== 'undefined' && navigator.msSaveOrOpenBlob) {
      navigator.msSaveOrOpenBlob(blob, filename);
    } else {
      const element = document.createElement('a');
      document.body.appendChild(element);

      const url = window.URL.createObjectURL(blob);
      element.setAttribute('href', url);
      element.setAttribute('download', filename);

      element.style.display = 'none';
      element.click();

      document.body.removeChild(element);
    }
  }

  parseErrorBlob(err: HttpErrorResponse): Observable<any> {
    const reader: FileReader = new FileReader();

    const obs = Observable.create((observer: any) => {
      reader.onloadend = (e) => {
        try {
          observer.error({'error': JSON.parse(reader.result)});
        } catch (e) {
          observer.error({'error': null});
        }

        observer.complete();
      };
    });
    reader.readAsText(err.error);
    return obs;
  }

  updatePassword(newPassword) {
    return this.http.patch(API_CONSTANT.set_password, newPassword);
  }

  confirmPassword(password) {
    return this.http.post(API_CONSTANT.confirm_password, password);
  }

  uploadCSV(document) {
    return this.http.post(API_CONSTANT.upload_csv, document);
  }

  signSignature(document) {
    return this.http.postBlob(API_CONSTANT.sign_docs, document).pipe(catchError(this.parseErrorBlob));
  }

  getBackgroundJobStatus(id) {
    return this.http.get(API_CONSTANT.background_jobs  + id + '/');
  }

  getTaxDataResult(data, params?) {
    let url = API_CONSTANT.tax_data_upload_result;

    if (params != null) {
      url += '?' + params;
    }

    return this.http.post(url, data);
  }

  postSummary(data) {
    return this.http.post(API_CONSTANT.summary_csv, data);
  }

  getSalesTaxReport(data) {
    return this.http.postBlob(API_CONSTANT.sales_tax_report, data).pipe(catchError(this.parseErrorBlob));
  }

  getZip3MB(data) {
    return this.http.postBlob(API_CONSTANT.zip_file_three_megabyte, data).pipe(catchError(this.parseErrorBlob));
  }

  getZip(data) {
    return this.http.getBlob(API_CONSTANT.zip_file, data).pipe(catchError(this.parseErrorBlob));
  }

  getFileFullPath(full_path) {
    return this.http.getBlob(full_path).pipe(catchError(this.parseErrorBlob));
  }

  postSearch(data, params?) {
    let url = API_CONSTANT.search_files;

    if (params != null)
      url += '?' + params;

    return this.http.post(url, data);
  }

  postSearch1(data, params?) {  // dummy function to make old code working
    return this.postSearch(data, params);
  }

  sign_counter(data, params?) {
    return this.http.post(API_CONSTANT.sign_counter + '?' + params, data);
  }

  getUsers(params?) {
    return this.http.get(API_CONSTANT.users, params);
  }

  createUser(data) {
    return this.http.post(API_CONSTANT.users, data);
  }

  updateUser(data) {
    return this.http.patch(API_CONSTANT.users + data.id + '/', data);
  }

  deleteUser(data) {
    return this.http.delete(API_CONSTANT.users + data.id + '/');
  }

  getRoles(params?) {
    return this.http.get(API_CONSTANT.groups, params);
  }

  getCompanyGroups(params?) {
    return this.http.get(API_CONSTANT.company_groups, params);
  }

  getMe(params?) {
    return this.http.get(API_CONSTANT.me, params);
  }

  getDashboardMonthlyData(data) {
    return this.http.get(API_CONSTANT.dashboard_monthly, data);
  }

  getDashboardSignCounterMinute(data) {
    return this.http.get(API_CONSTANT.dashboard_sign_counter_minute, data);
  }

  getCompany() {
    return this.http.get(API_CONSTANT.corporates);
  }

  getBranch() {
    return this.http.get(API_CONSTANT.branches);
  }

  postSaleTax(data, params?) {
    let url = API_CONSTANT.sales_tax_view;

    if (params != null)
      url += '?' + params;

    return this.http.post(url, data);
  }

  postBulkSaleTax(data, params?) {
    let url = API_CONSTANT.bulk_sales_tax_view;

    if (params != null) {
       url += '?' + params;
    }

    return this.http.post(url, data);
  }

  getErrors(params?) {
     return this.http.get(API_CONSTANT.get_errors, params);
  }

  postSaleZipTax(data, params?) {
    let url = API_CONSTANT.sales_zip_tax;

    if (params != null)
      url += '?' + params;

    return this.http.post(url, data);
  }

  postSubmitToRd(data, params?) {
    let url = API_CONSTANT.submit_to_rd;

    if (params != null) {
       url += '?' + params;
    }

    return this.http.post(url, data);
  }


  getMissingDoc(params) {
    return this.http.getBlob(API_CONSTANT.export_missing_excel, params).pipe(catchError(this.parseErrorBlob));
  }

  getBulkMissingDoc(params) {
    return this.http.getBlob(API_CONSTANT.export_bulk_missing_excel, params).pipe(catchError(this.parseErrorBlob));
  }

  approveSale(params) {
    return this.http.post(API_CONSTANT.approve_sale_tax, params);
  }

  approveUploadSale(params) {
    return this.http.post(API_CONSTANT.approve_upload_sale, params);
  }

  updateSettings(params) {
    return this.http.post(API_CONSTANT.update_settings, params);
  }

  getSettings() {
    return this.http.get(API_CONSTANT.get_settings);
  }

  getAllDocs(params) {
    return this.http.getBlob(API_CONSTANT.export_all_docs, params).pipe(catchError(this.parseErrorBlob));
  }

  getBulkAllDocs(params) {
    return this.http.getBlob(API_CONSTANT.export_bulk_all_docs, params).pipe(catchError(this.parseErrorBlob));
  }

  downloadFileFromMediaPath(url) {
    return this.http.getBlob(API_CONSTANT.MEDIA_PATH_URL + url).pipe(catchError(this.parseErrorBlob));
  }

  getAllZipFile(params) {
    return this.http.postBlob(API_CONSTANT.export_zip_file, params).pipe(catchError(this.parseErrorBlob));
  }

  getAllExcelFile(params) {
    return this.http.postBlob(API_CONSTANT.export_excel_submit_to_rd, params).pipe(catchError(this.parseErrorBlob));
  }

  getExcelReport(params) {
    return this.http.getBlob(API_CONSTANT.export_monthly_excel_report, params).pipe(catchError(this.parseErrorBlob));
  }

  submitCheckedToRd(params) {
    return this.http.post(API_CONSTANT.submit_checked_to_rd, params);
  }

  getEmailDashboard(params?) {
    return this.http.get(API_CONSTANT.email_report, params);
  }

  getBuyer(params?) {
    return this.http.get(API_CONSTANT.buyer, params);
  }

  getNewBuyer() {
    return this.http.get(API_CONSTANT.buyer);
  }

  getExternalBuyerData(params) {
    return this.http.get(`${API_CONSTANT.buyer}external/`, params);
  }

  getBuyerBranch(params?) {
    return this.http.get(API_CONSTANT.buyer_branch, params);
  }

  getSellerBranch(params?) {
    return this.http.get(API_CONSTANT.seller_branch, params);
  }

  postB2PDocumentSummary(data, params?) {
    let url = API_CONSTANT.b2p_document_summary;

    if (params != null) {
      url += '?' + params;
    }
    return this.http.post(url, data);
  }

  postB2PDocumentSummaryDetail(data, params?) {
    let url = API_CONSTANT.b2p_document_summary_detail;

    if (params != null) {
      url += '?' + params;
    }
    return this.http.post(url, data);
  }

  downloadB2PDocumentSummaryDetailExcel(data, params?) {
    let url = API_CONSTANT.b2p_document_summary_detail_excel;

    if (params != null) {
      url += '?' + params;
    }

    return this.http.postBlob(url, data).pipe(catchError(this.parseErrorBlob));
  }

  getBillingExcelReport(params) {
    return this.http.getBlob(API_CONSTANT.export_sign_counter_excel_monthly_report, params).pipe(catchError(this.parseErrorBlob));
  }

  getActionsLogExcelReport(params) {
    return this.http.getBlob(API_CONSTANT.export_actions_log_excel_report, params).pipe(catchError(this.parseErrorBlob));
  }

  getCountry(params?) {
    return this.http.get(API_CONSTANT.country, params);
  }

  getAllBuyerEmailsReport() {
    return this.http.get(API_CONSTANT.all_buyer_emails_report);
  }

  getAllImportMethods() {
    return this.http.get(API_CONSTANT.all_import_methods);
  }

  createBuyerBranch(data) {
    return this.http.post(API_CONSTANT.buyer_branch, data);
  }

  updateBuyerBranch(id, data) {
    return this.http.patch(API_CONSTANT.buyer_branch + id + '/', data);
  }

  createSellerBranch(data) {
    return this.http.post(API_CONSTANT.seller_branch, data);
  }

  updateSellerBranch(id, data) {
    return this.http.patch(API_CONSTANT.seller_branch + id + '/', data);
  }

  createETaxInvoice(data) {
    return this.http.post(API_CONSTANT.create_etax_invoice, data);
  }

  sendEmailETaxInvoice(data) {
    return this.http.post(API_CONSTANT.send_email_etax_invoice, data);
  }

  checkMatchedInvoiceNumber(params?) {
    return this.http.get(API_CONSTANT.matched_invoice_number, params);
  }

  // RD Send Report
  getRDSendReport(params?) {
    return this.http.get(API_CONSTANT.rd_send_results, params);
  }

  resendToRD(taxDataId: number) {
    return this.http.post(API_CONSTANT.rd_send_results + taxDataId + '/resend/', {});
  }

  resendAllPendingEbxml(data) {
    return this.http.post(API_CONSTANT.resend_all_pending_ebxml, data);
  }

  getLatestManualResendTime(data) {
    return this.http.get(API_CONSTANT.get_latest_manual_resend_time, data);
  }

  // Logout
  logout() {
    return this.http.post(API_CONSTANT.logout, null);
  }

  sniff(data) {
    return this.http.post(API_CONSTANT.history_log, data);
  }

  getActions() {
    return this.http.get(API_CONSTANT.history_log_type);
  }

  getSniffs(params?) {
    return this.http.get(API_CONSTANT.history_log, params);
  }

  getDocumentRequest(params?) {
    return this.http.get(API_CONSTANT.document_request, params);
  }

  getDocumentResponseDetail(id) {
    return this.http.get(`${API_CONSTANT.document_request}${id}/`)
  }

  cancelDocumentRequest(id) {
    return this.http.post(`${API_CONSTANT.document_request}${id}/cancel/`, {})
  }

  restoreDocumentRequest(id) {
    return this.http.post(`${API_CONSTANT.document_request}${id}/restore/`, {})
  }

  deleteDocumentRequest(id) {
    return this.http.delete(`${API_CONSTANT.document_request}${id}/`)
  }

  getDocumentRequestDropdown(params?) {
    return this.http.get(API_CONSTANT.document_request_dropdown, params);
  }

  createDocumentRequest(params) {
    return this.http.post(API_CONSTANT.document_request, params);
  }
}



// WEBPACK FOOTER //
// /opt/app/src/app/services/api.service.ts